import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useChatSocket } from '../contexts/ChatSocketContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './Chat.css';
import {
  Layout,
  Input,
  Avatar,
  Typography,
  Space,
  Empty,
  Spin,
  Button,
  Modal,
  Badge,
  Dropdown,
  Menu,
  Tooltip,
  Divider,
  List,
  Popover,
  Checkbox,
  message,
  AutoComplete,
  Select,
  DatePicker,
  Form,
  ColorPicker,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  SmileOutlined,
  PaperClipOutlined,
  SendOutlined,
  MoreOutlined,
  StarOutlined,
  StarFilled,
  PushpinOutlined,
  CheckCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined,
  CommentOutlined,
  InfoCircleOutlined,
  BellOutlined,
  NumberOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

dayjs.extend(relativeTime);

const { Content, Sider } = Layout;
const { Text, Paragraph } = Typography;

// Helper function to render avatar with profile image support
function renderAvatar(user: any, size: number = 32, accentColor: string = '#1890ff') {
  const avatarUrl = user?.avatar_url || user?.user?.avatar_url;
  const firstName = user?.first_name || user?.user?.first_name || '';
  const lastName = user?.last_name || user?.user?.last_name || '';
  
  return (
    <Avatar
      size={size}
      src={avatarUrl ? `/api${avatarUrl}` : undefined}
      style={{ backgroundColor: accentColor }}
    >
      {firstName[0]}{lastName[0]}
    </Avatar>
  );
}

// Simple markdown renderer for messages
function renderMarkdown(text: string): React.ReactNode {
  if (!text) return text;
  
  // Escape HTML first
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  
  // Code: `code`
  html = html.replace(/`([^`]+)`/g, '<code style="background: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>');
  
  // Code blocks: ```code```
  html = html.replace(/```([^`]+)```/g, '<pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; overflow-x: auto;"><code>$1</code></pre>');
  
  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff;">$1</a>');
  
  // URLs: http://... or https://...
  html = html.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #1890ff;">$1</a>');
  
  // Mentions: @username or @First Last
  // Match @ followed by word characters and spaces (for full names)
  html = html.replace(/@([A-Za-z]+(?:\s+[A-Za-z]+)?)/g, '<span style="background-color: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; font-weight: 500;">@$1</span>');
  
  // Line breaks
  html = html.replace(/\n/g, '<br />');
  
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// Extract mentions from text
function extractMentions(text: string): string[] {
  const mentionPattern = /@(\w+)/g;
  const mentions: string[] = [];
  let match;
  while ((match = mentionPattern.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  return mentions;
}

// Extract URLs from text
function extractUrls(text: string): string[] {
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const urls: string[] = [];
  let match;
  while ((match = urlPattern.exec(text)) !== null) {
    urls.push(match[1]);
  }
  return urls;
}

// Link Preview Component
function LinkPreview({ url }: { url: string }) {
  const domain = url.replace(/^https?:\/\//, '').split('/')[0];
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        marginTop: 8,
        padding: '12px',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        textDecoration: 'none',
        color: '#1d1c1d',
        backgroundColor: '#fafafa',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1890ff';
        e.currentTarget.style.backgroundColor = '#f0f7ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e0e0e0';
        e.currentTarget.style.backgroundColor = '#fafafa';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ 
          width: 48, 
          height: 48, 
          backgroundColor: '#1890ff', 
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Text style={{ color: '#fff', fontSize: 20, fontWeight: 'bold' }}>
            {domain[0].toUpperCase()}
          </Text>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
            {domain}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {url}
          </Text>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>↗</Text>
        </div>
      </div>
    </a>
  );
}

interface ChatProps {}

const Chat: React.FC<ChatProps> = () => {
  const { user } = useAuth();
  const { socket, isConnected, isConnecting, connectionError, userPresence, reconnect } = useChatSocket();
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [createRoomModalVisible, setCreateRoomModalVisible] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showGroupNameInput, setShowGroupNameInput] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [reactionPopoverVisible, setReactionPopoverVisible] = useState<string | null>(null);
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [isPrivateGroup, setIsPrivateGroup] = useState(false);
  const [threadRootId, setThreadRootId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteRoomId, setInviteRoomId] = useState<string | null>(null);
  const [inviteUserIds, setInviteUserIds] = useState<string[]>([]);
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [publicGroupsModalVisible, setPublicGroupsModalVisible] = useState(false);
  const [publicGroupsSearchQuery, setPublicGroupsSearchQuery] = useState('');
  const [messageSearchVisible, setMessageSearchVisible] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [pinnedMessagesModalVisible, setPinnedMessagesModalVisible] = useState(false);
  const [roomInfoVisible, setRoomInfoVisible] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const [mentionOptions, setMentionOptions] = useState<any[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [searchFilterUserId, setSearchFilterUserId] = useState<string | undefined>();
  const [searchDateRange, setSearchDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [searchContentType, setSearchContentType] = useState<string | undefined>();
  const [customStatusModalVisible, setCustomStatusModalVisible] = useState(false);
  const [customStatusText, setCustomStatusText] = useState('');
  const [notificationSettingsModalVisible, setNotificationSettingsModalVisible] = useState(false);
  const [themeModalVisible, setThemeModalVisible] = useState(false);
  const [themeColor, setThemeColor] = useState('#1a1a2e');
  const [themeAccentColor, setThemeAccentColor] = useState('#1890ff');
  const [inviteConfirmationVisible, setInviteConfirmationVisible] = useState(false);
  const [usersToInvite, setUsersToInvite] = useState<any[]>([]);
  const [pendingMessage, setPendingMessage] = useState<{ room_id: string; content: string; attachments?: string[] } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch user's chat rooms
  const { data: roomsData, isLoading: roomsLoading, error: roomsError } = useQuery(
    'chat-rooms',
    () => api.get('/api/chat/rooms').then(res => res.data),
    {
      enabled: !!user && !!user.id,
      refetchInterval: 10000, // Refetch every 10 seconds for unread counts
      retry: 2,
      onError: (error: any) => {
        console.error('Error fetching chat rooms:', error);
      },
    }
  );

  const rooms = roomsData?.rooms || [];
  const directMessages = rooms.filter((r: any) => r.type === 'direct');
  const groupRooms = rooms.filter((r: any) => r.type === 'group');
  const starredRooms = rooms.filter((r: any) => r.is_starred);
  const nonStarredGroupRooms = groupRooms.filter((r: any) => !r.is_starred);
  const nonStarredDirectMessages = directMessages.filter((r: any) => !r.is_starred);

  // Fetch messages for selected room
  const { data: messagesData, isLoading: messagesLoading, refetch: refetchMessages } = useQuery(
    ['chat-messages', selectedRoomId],
    () => {
      console.log('Fetching messages for room:', selectedRoomId);
      return api.get(`/api/chat/rooms/${selectedRoomId}/messages`).then(res => {
        console.log('Messages received:', res.data);
        return res.data;
      });
    },
    {
      enabled: !!selectedRoomId && !!user && !!user.id,
      refetchInterval: false, // Disable auto-refetch - rely on socket.io for real-time updates
      refetchOnWindowFocus: true,
    }
  );

  const messages = messagesData?.messages || [];

  // Fetch available users for creating rooms
  const { data: usersData } = useQuery(
    'chat-users',
    () => api.get('/api/chat/users').then(res => res.data),
    {
      enabled: !!user && !!user.id,
    }
  );

  const availableUsers = usersData?.users || [];

  // Fetch public groups for search
  const { data: publicGroupsData, refetch: refetchPublicGroups, isLoading: publicGroupsLoading } = useQuery(
    ['public-groups', publicGroupsSearchQuery],
    () => api.get(`/api/chat/rooms/public/search?q=${encodeURIComponent(publicGroupsSearchQuery || '')}`).then(res => res.data),
    {
      enabled: publicGroupsModalVisible && !!user && !!user.id,
      refetchOnWindowFocus: false,
    }
  );

  const publicGroups = publicGroupsData?.rooms || [];

  // Star/unstar room mutation
  const starRoomMutation = useMutation(
    async ({ roomId, star }: { roomId: string; star: boolean }) => {
      const response = await api.put(`/api/chat/rooms/${roomId}/star`, { star });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chat-rooms');
      },
    }
  );

  const handleStarRoom = (roomId: string, currentStarred: boolean) => {
    starRoomMutation.mutate({ roomId, star: !currentStarred });
  };

  // Fetch custom status and theme preferences
  useEffect(() => {
    if (user?.id) {
      api.get('/api/chat/user/presence')
        .then(res => {
          if (res.data?.custom_status) {
            setCustomStatusText(res.data.custom_status);
          }
        })
        .catch(() => {
          // Ignore errors
        });
      
      // Fetch theme preferences
      api.get('/api/users/preferences')
        .then(res => {
          if (res.data?.theme_color) {
            setThemeColor(res.data.theme_color);
          }
          if (res.data?.theme_accent_color) {
            setThemeAccentColor(res.data.theme_accent_color);
          }
        })
        .catch(() => {
          // Ignore errors, use defaults
        });
    }
  }, [user?.id]);

  // Debounce search query
  useEffect(() => {
    if (!publicGroupsModalVisible) return;
    
    const timer = setTimeout(() => {
      refetchPublicGroups();
    }, 300);

    return () => clearTimeout(timer);
  }, [publicGroupsSearchQuery, publicGroupsModalVisible, refetchPublicGroups]);

  // Auto-scroll to bottom when new messages arrive or thread updates
  useEffect(() => {
    if (messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, threadRootId]);

  // Socket event listeners
  useEffect(() => {
    if (!socket || !selectedRoomId) return;

    const handleNewMessage = (data: any) => {
      if (data.room_id === selectedRoomId) {
        queryClient.invalidateQueries(['chat-messages', selectedRoomId]);
        queryClient.invalidateQueries('chat-rooms');
      }
    };
    
    const handleMessageNew = (data: any) => {
      console.log('Received message:new event:', data);
      const messageRoomId = data.room_id || data.roomId;
      
      // If thread reply, update parent message in cache
      if (data.thread_root_id && messageRoomId === selectedRoomId) {
        queryClient.setQueryData(['chat-messages', selectedRoomId], (old: any) => {
          if (!old || !old.messages) return old;
          return {
            messages: old.messages.map((msg: any) => {
              if (msg.id === data.thread_root_id) {
                // Add new thread reply to parent
                const updatedReplies = [...(msg.thread_replies || []), {
                  ...data,
                  first_name: data.user?.first_name || data.first_name,
                  last_name: data.user?.last_name || data.last_name,
                  email: data.user?.email || data.email
                }];
                return { ...msg, thread_replies: updatedReplies };
              }
              return msg;
            })
          };
        });
        // Don't add thread replies to main message list, but update unread counts
        if (messageRoomId !== selectedRoomId) {
          queryClient.invalidateQueries('chat-rooms');
        }
        return;
      }
      
      // Only add non-thread messages to main list
      if (!data.thread_root_id && messageRoomId === selectedRoomId) {
        console.log('Adding new message to room:', selectedRoomId);
        queryClient.setQueryData(['chat-messages', selectedRoomId], (old: any) => {
          if (!old || !old.messages) {
            return { messages: [{
              ...data,
              first_name: data.user?.first_name || data.first_name,
              last_name: data.user?.last_name || data.last_name,
              email: data.user?.email || data.email,
              thread_replies: []
            }] };
          }
          // Check if message already exists
          const exists = old.messages.some((msg: any) => msg.id === data.id);
          if (exists) {
            // Update existing message with thread_replies if provided
            return {
              messages: old.messages.map((msg: any) => 
                msg.id === data.id ? {
                  ...msg,
                  ...data,
                  first_name: data.user?.first_name || data.first_name || msg.first_name,
                  last_name: data.user?.last_name || data.last_name || msg.last_name,
                  email: data.user?.email || data.email || msg.email,
                  thread_replies: data.thread_replies || msg.thread_replies || []
                } : msg
              )
            };
          }
          return {
            messages: [...old.messages, {
              ...data,
              first_name: data.user?.first_name || data.first_name,
              last_name: data.user?.last_name || data.last_name,
              email: data.user?.email || data.email,
              thread_replies: []
            }]
          };
        });
      }
      
      // Always update room list for unread counts (but only if not viewing that room)
      if (messageRoomId !== selectedRoomId) {
        queryClient.invalidateQueries('chat-rooms');
      }
    };
    
    const handleThreadUpdate = (data: any) => {
      console.log('Received message:thread-update event:', data);
      const messageRoomId = data.room_id || data.roomId;
      if (messageRoomId === selectedRoomId) {
        // Update parent message with new thread replies
        queryClient.setQueryData(['chat-messages', selectedRoomId], (old: any) => {
          if (!old || !old.messages) return old;
          return {
            messages: old.messages.map((msg: any) => {
              if (msg.id === data.id) {
                return { ...msg, thread_replies: data.thread_replies || [] };
              }
              return msg;
            })
          };
        });
      }
      // Only update unread counts if not viewing that room
      if (messageRoomId !== selectedRoomId) {
        queryClient.invalidateQueries('chat-rooms');
      }
    };

    const handleTyping = (data: any) => {
      if (data.room_id === selectedRoomId) {
        setTypingUsers(prev => new Set(prev).add(data.user_id));
        setTimeout(() => {
          setTypingUsers(prev => {
            const next = new Set(prev);
            next.delete(data.user_id);
            return next;
          });
        }, 3000);
      }
    };

    const handleStopTyping = (data: any) => {
      if (data.room_id === selectedRoomId) {
        setTypingUsers(prev => {
          const next = new Set(prev);
          next.delete(data.user_id);
          return next;
        });
      }
    };

    const handleMessagePinned = (data: any) => {
      if (data.room_id === selectedRoomId) {
        queryClient.setQueryData(['chat-messages', selectedRoomId], (old: any) => {
          if (!old || !old.messages) return old;
          return {
            messages: old.messages.map((msg: any) =>
              msg.id === data.message_id ? { ...msg, is_pinned: data.is_pinned } : msg
            )
          };
        });
      }
    };

    const handleSocketError = (error: any) => {
      if (error.code === 'USERS_NOT_IN_ROOM' && error.users) {
        // Store pending message and show invite confirmation
        setUsersToInvite(error.users);
        setInviteConfirmationVisible(true);
      } else {
        message.error(error.message || 'An error occurred');
      }
    };

    socket.on('message', handleNewMessage);
    socket.on('message:new', handleMessageNew);
    socket.on('message:thread-update', handleThreadUpdate);
    socket.on('message:pinned', handleMessagePinned);
    socket.on('error', handleSocketError);
    socket.on('typing', handleTyping);
    socket.on('typing:start', handleTyping);
    socket.on('typing:stop', handleStopTyping);

    return () => {
      socket.off('message', handleNewMessage);
      socket.off('message:new', handleMessageNew);
      socket.off('message:thread-update', handleThreadUpdate);
      socket.off('message:pinned', handleMessagePinned);
      socket.off('error', handleSocketError);
      socket.off('typing', handleTyping);
      socket.off('typing:start', handleTyping);
      socket.off('typing:stop', handleStopTyping);
    };
  }, [socket, selectedRoomId, queryClient]);


  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Desktop notifications for new messages
  useEffect(() => {
    if (!socket || !user) return;

    const handleNotificationMessage = (data: any) => {
      // Don't notify for own messages or if viewing the room
      if (data.user_id === user.id || data.room_id === selectedRoomId) {
        return;
      }

      // Check if user is mentioned
      const isMentioned = data.mentions && data.mentions.includes(user.id);
      
      if ('Notification' in window && Notification.permission === 'granted') {
        const roomName = rooms.find((r: any) => r.id === data.room_id)?.name || 
                        (data.room_type === 'direct' ? 'Direct Message' : 'Channel');
        const senderName = data.first_name && data.last_name 
          ? `${data.first_name} ${data.last_name}`
          : 'Someone';
        
        const notification = new Notification(
          isMentioned ? `@${senderName} mentioned you in ${roomName}` : `New message in ${roomName}`,
          {
            body: data.content?.substring(0, 100) || 'New message',
            icon: '/favicon.ico',
            tag: `chat-${data.room_id}-${data.id}`, // Prevent duplicate notifications
            requireInteraction: isMentioned, // Keep mention notifications visible
          }
        );

        notification.onclick = () => {
          window.focus();
          if (data.room_id !== selectedRoomId) {
            setSelectedRoomId(data.room_id);
          }
          notification.close();
        };
      }
    };

    socket.on('message:new', handleNotificationMessage);
    socket.on('message:mention', (data: any) => {
      if ('Notification' in window && Notification.permission === 'granted') {
        const senderName = data.message?.first_name && data.message?.last_name
          ? `${data.message.first_name} ${data.message.last_name}`
          : 'Someone';
        
        new Notification(
          `@${senderName} mentioned you`,
          {
            body: data.message?.content?.substring(0, 100) || 'New mention',
            icon: '/favicon.ico',
            requireInteraction: true,
          }
        );
      }
    });

    return () => {
      socket.off('message:new', handleNotificationMessage);
      socket.off('message:mention');
    };
  }, [socket, user, selectedRoomId, rooms]);

  // Join room when selected and mark messages as read
  useEffect(() => {
    if (socket && selectedRoomId && isConnected) {
      socket.emit('room:join', { room_id: selectedRoomId });
      
      // Mark all messages in this room as read
      socket.emit('message:read', { room_id: selectedRoomId });
      
      // Update room list to clear unread count
      queryClient.invalidateQueries('chat-rooms');
      
      return () => {
        socket.emit('room:leave', { room_id: selectedRoomId });
      };
    }
  }, [socket, selectedRoomId, isConnected, queryClient]);

  // Create room mutation
  const createRoomMutation = useMutation(
    (data: { name?: string; type: 'direct' | 'group'; user_ids: string[]; is_private?: boolean }) =>
      api.post('/api/chat/rooms', data).then(res => res.data),
    {
      onSuccess: async (data) => {
        setCreateRoomModalVisible(false);
        setSelectedUserIds([]);
        setUserSearchQuery('');
        setShowGroupNameInput(false);
        setGroupName('');
        setIsPrivateGroup(false);
        
        try {
          await queryClient.invalidateQueries('chat-rooms');
          await queryClient.refetchQueries('chat-rooms');
        } catch (error) {
          console.error('Error refreshing chat rooms:', error);
        }
        
        if (data.room?.id) {
          setSelectedRoomId(data.room.id);
        } else if (data.room_id) {
          setSelectedRoomId(data.room_id);
        }
      },
      onError: (error: any) => {
        console.error('Create room error:', error);
      },
    }
  );

  // Send message mutation
  const sendMessageMutation = useMutation(
    async (data: { room_id: string; content: string; attachments?: string[] }) => {
      if (data.attachments && data.attachments.length > 0) {
        const uploadPromises = data.attachments.map((fileId: string) => {
          const file = selectedFiles.find((f: File) => f.name === fileId);
          if (!file) return Promise.resolve(null);
          
          const formData = new FormData();
          formData.append('file', file);
          return api.post('/api/chat/attachments', formData).then(res => res.data.filename);
        });
        
        const filenames = await Promise.all(uploadPromises);
        data.attachments = filenames.filter(Boolean) as string[];
      }
      
      if (socket && socket.connected) {
        console.log('Sending message via socket:', {
          room_id: data.room_id,
          content: data.content,
          content_type: data.attachments && data.attachments.length > 0 ? 'attachment' : 'text',
        });
        socket.emit('message:send', {
          room_id: data.room_id,
          content: data.content,
          content_type: data.attachments && data.attachments.length > 0 ? 'attachment' : 'text',
          attachment_url: data.attachments && data.attachments.length > 0 ? data.attachments[0] : null,
          attachment_name: data.attachments && data.attachments.length > 0 ? data.attachments[0] : null,
          reply_to: threadRootId || null,
          thread_root_id: threadRootId || null,
        });
        // Wait a bit for the message to be processed
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.error('Socket not connected, cannot send message');
        throw new Error('Socket not connected');
      }
      return Promise.resolve();
    },
    {
      onSuccess: async () => {
        setMessageText('');
        setSelectedFiles([]);
        setUploadingFiles(false);
        setThreadRootId(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (socket && selectedRoomId) {
          socket.emit('typing:stop', { room_id: selectedRoomId });
        }
        // Immediately refetch messages to show the new message
        if (selectedRoomId) {
          console.log('Refetching messages after send for room:', selectedRoomId);
          // Wait a moment for the backend to process
          setTimeout(async () => {
            await queryClient.refetchQueries(['chat-messages', selectedRoomId]);
            await queryClient.invalidateQueries('chat-rooms');
          }, 300);
        }
      },
      onError: (error: any) => {
        console.error('Send message error:', error);
      },
    }
  );

  const handleCreateRoom = (roomType: 'direct' | 'group', groupName?: string) => {
    if (selectedUserIds.length === 0) return;
    if (roomType === 'direct' && selectedUserIds.length !== 1) return;
    if (roomType === 'group' && !groupName?.trim()) return;
    
    createRoomMutation.mutate({
      name: roomType === 'group' ? groupName : undefined,
      type: roomType,
      user_ids: selectedUserIds,
      is_private: roomType === 'group' ? isPrivateGroup : false,
    });
  };
  
  const insertEmoji = (emoji: string) => {
    setMessageText(prev => prev + emoji);
    setEmojiPickerVisible(false);
  };
  
  const canEditDeleteMessage = (message: any) => {
    if (message.user_id !== user?.id) return false;
    const createdAt = dayjs(message.created_at);
    const now = dayjs();
    const diffMinutes = now.diff(createdAt, 'minute');
    return diffMinutes <= 15;
  };
  
  const handleUserSelect = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };
  
  const filteredAvailableUsers = availableUsers.filter((u: any) => {
    if (!user || u.id === user.id) return false;
    const searchLower = userSearchQuery.toLowerCase();
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const handleSendMessage = () => {
    if (!selectedRoomId || (!messageText.trim() && selectedFiles.length === 0)) return;

    const fileIds = selectedFiles.map((f: File) => f.name);
    // Store message in case we need to resend after inviting users
    const messageData = {
      room_id: selectedRoomId,
      content: messageText.trim(),
      attachments: fileIds.length > 0 ? fileIds : undefined,
    };
    setPendingMessage(messageData);
    sendMessageMutation.mutate(messageData);
  };

  const handleInviteAndResend = async () => {
    if (!pendingMessage || !selectedRoomId) return;
    
    try {
      // Invite users to the room
      await api.post(`/api/chat/rooms/${selectedRoomId}/invite`, {
        user_ids: usersToInvite.map((u: any) => u.id)
      });
      
      // Refresh rooms list
      queryClient.invalidateQueries('chat-rooms');
      
      // Close modal and resend message
      setInviteConfirmationVisible(false);
      const userCount = usersToInvite.length;
      setUsersToInvite([]);
      
      // Resend the message
      sendMessageMutation.mutate(pendingMessage);
      setPendingMessage(null);
      
      message.success(`Invited ${userCount} user(s) and sent message`);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to invite users');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const filteredRooms = rooms.filter((room: any) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (room.type === 'direct' && room.other_user) {
      const name = `${room.other_user.first_name} ${room.other_user.last_name}`.toLowerCase();
      return name.includes(query);
    }
    return room.name?.toLowerCase().includes(query);
  });

  const selectedRoom = rooms.find((r: any) => r.id === selectedRoomId);

  if (!user || !user.id) {
    return <Empty description="Please log in to access chat" />;
  }

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#fff' }}>
      {/* Left Sidebar - Slack Style */}
      <Sider
        width={260}
        style={{
          background: themeColor,
          color: '#fff',
          overflow: 'auto',
        }}
        theme="dark"
      >
        {/* Workspace Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong style={{ color: '#fff', fontSize: 16 }}>
              Patel HRMS
            </Text>
            <Space>
              <Button
                type="text"
                icon={<PlusOutlined />}
                onClick={() => setCreateRoomModalVisible(true)}
                style={{ color: '#fff' }}
                title="New conversation"
              />
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                onClick={() => setThemeModalVisible(true)}
                style={{ color: '#fff' }}
                title="Customize theme"
              />
            </Space>
          </Space>
          {/* User Info - Status hidden for now */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px',
              borderRadius: 4,
              background: 'rgba(255,255,255,0.1)',
            }}
          >
            {renderAvatar(user, 24, themeAccentColor)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: '#fff', fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.first_name} {user?.last_name}
              </Text>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '8px 12px' }}>
          <Input
            placeholder="Search conversations..."
            prefix={<SearchOutlined style={{ color: '#fff' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#fff',
            }}
            className="chat-search-input"
          />
        </div>

        {/* Favorites Section */}
        {starredRooms.length > 0 && (
          <div style={{ padding: '8px 12px' }}>
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600, textTransform: 'uppercase' }}>
              Favorites
            </Text>
            <div style={{ marginTop: 8 }}>
              {starredRooms.map((room: any) => {
                if (room.type === 'direct') {
                  const otherUser = room.other_user;
                  const presence = otherUser ? userPresence[otherUser.id] : null;
                  return (
                    <div
                      key={room.id}
                      onClick={() => setSelectedRoomId(room.id)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: selectedRoomId === room.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 2,
                      }}
                    >
                      <Avatar size={20} style={{ backgroundColor: '#1890ff' }}>
                        {otherUser ? `${otherUser.first_name[0]}${otherUser.last_name[0]}` : '?'}
                      </Avatar>
                      <Text style={{ color: '#fff', fontSize: 14, flex: 1 }}>
                        {otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : 'Unknown'}
                      </Text>
                      {room.unread_count > 0 && selectedRoomId !== room.id && (
                        <Badge count={room.unread_count} size="small" style={{ backgroundColor: '#52c41a' }} />
                      )}
                      <Button
                        type="text"
                        size="small"
                        icon={<StarFilled style={{ color: '#faad14' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStarRoom(room.id, true);
                        }}
                        style={{ color: '#faad14', opacity: 0.7 }}
                        title="Unstar"
                      />
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={room.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRoomId(room.id);
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: selectedRoomId === room.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 2,
                      }}
                    >
                      {room.is_private ? (
                        <LockOutlined style={{ fontSize: 14, color: '#ffa940' }} />
                      ) : (
                        <UnlockOutlined style={{ fontSize: 14, color: '#52c41a' }} />
                      )}
                      <Text style={{ color: '#fff', fontSize: 14, flex: 1 }}>
                        {room.name || 'Unnamed Channel'}
                      </Text>
                      {room.unread_count > 0 && selectedRoomId !== room.id && (
                        <Badge count={room.unread_count} size="small" style={{ backgroundColor: '#52c41a' }} />
                      )}
                      <Button
                        type="text"
                        size="small"
                        icon={<StarFilled style={{ color: '#faad14' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStarRoom(room.id, true);
                        }}
                        style={{ color: '#faad14', opacity: 0.7 }}
                        title="Unstar"
                      />
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}

        {/* Channels Section */}
        <div style={{ padding: '8px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600, textTransform: 'uppercase' }}>
              Channels
            </Text>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setPublicGroupsModalVisible(true)}
              style={{ color: '#fff', fontSize: 12 }}
              title="Browse public groups"
            >
              Browse
            </Button>
          </div>
          {roomsLoading ? (
            <Spin size="small" style={{ marginTop: 8, color: '#fff' }} />
          ) : (
            <div style={{ marginTop: 8 }}>
              {nonStarredGroupRooms.map((room: any) => (
                <div
                  key={room.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRoomId(room.id);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (user?.role === 'admin') {
                      Modal.confirm({
                        title: 'Delete Group',
                        content: `Are you sure you want to delete "${room.name || 'Unnamed Channel'}"? This action cannot be undone.`,
                        okText: 'Delete',
                        okType: 'danger',
                        onOk: async () => {
                          try {
                            await api.delete(`/api/chat/rooms/${room.id}`);
                            queryClient.invalidateQueries('chat-rooms');
                            if (selectedRoomId === room.id) {
                              setSelectedRoomId(null);
                            }
                          } catch (error) {
                            console.error('Failed to delete room:', error);
                          }
                        },
                      });
                    }
                  }}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    background: selectedRoomId === room.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  {room.is_private ? (
                    <LockOutlined style={{ fontSize: 14, color: '#ffa940' }} />
                  ) : (
                    <UnlockOutlined style={{ fontSize: 14, color: '#52c41a' }} />
                  )}
                  <Text style={{ color: '#fff', fontSize: 14, flex: 1 }}>
                    {room.name || 'Unnamed Channel'}
                  </Text>
                  {room.unread_count > 0 && selectedRoomId !== room.id && (
                    <Badge count={room.unread_count} size="small" style={{ backgroundColor: '#52c41a' }} />
                  )}
                  <Button
                    type="text"
                    size="small"
                    icon={<UserAddOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInviteRoomId(room.id);
                      setInviteModalVisible(true);
                    }}
                    style={{ color: '#fff', opacity: 0.7 }}
                    title="Invite users"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<StarOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStarRoom(room.id, false);
                    }}
                    style={{ color: '#fff', opacity: 0.7 }}
                    title="Star"
                  />
                  {user?.role === 'admin' && (
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      danger
                      onClick={(e) => {
                        e.stopPropagation();
                        Modal.confirm({
                          title: 'Delete Group',
                          content: `Are you sure you want to delete "${room.name || 'Unnamed Channel'}"? This action cannot be undone.`,
                          okText: 'Delete',
                          okType: 'danger',
                          onOk: async () => {
                            try {
                              await api.delete(`/api/chat/rooms/${room.id}`);
                              queryClient.invalidateQueries('chat-rooms');
                              if (selectedRoomId === room.id) {
                                setSelectedRoomId(null);
                              }
                            } catch (error) {
                              console.error('Failed to delete room:', error);
                            }
                          },
                        });
                      }}
                      style={{ color: '#fff', opacity: 0.7 }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Direct Messages Section */}
        <div style={{ padding: '8px 12px', marginTop: 16 }}>
          <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600, textTransform: 'uppercase' }}>
            Direct Messages
          </Text>
          {roomsLoading ? (
            <Spin size="small" style={{ marginTop: 8, color: '#fff' }} />
          ) : (
            <div style={{ marginTop: 8 }}>
              {nonStarredDirectMessages.map((room: any) => {
                const otherUser = room.other_user;
                const presence = otherUser ? userPresence[otherUser.id] : null;
                return (
                  <div
                    key={room.id}
                    onClick={() => setSelectedRoomId(room.id)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: selectedRoomId === room.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    <Avatar size={20} style={{ backgroundColor: '#1890ff' }}>
                      {otherUser ? `${otherUser.first_name[0]}${otherUser.last_name[0]}` : '?'}
                    </Avatar>
                    <Text style={{ color: '#fff', fontSize: 14, flex: 1 }}>
                      {otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : 'Unknown'}
                    </Text>
                    {room.unread_count > 0 && selectedRoomId !== room.id && (
                      <Badge count={room.unread_count} size="small" style={{ backgroundColor: '#52c41a' }} />
                    )}
                    <Button
                      type="text"
                      size="small"
                      icon={<StarOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStarRoom(room.id, false);
                      }}
                      style={{ color: '#fff', opacity: 0.7 }}
                      title="Star"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Sider>

      {/* Main Content Area */}
      <Layout style={{ background: '#fff' }}>
        <Content style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {selectedRoomId ? (
            <>
              {/* Header */}
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e0e0e0',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Space>
                  {selectedRoom?.type === 'direct' ? (
                    <Avatar size={32} style={{ backgroundColor: '#1890ff' }}>
                      {selectedRoom?.other_user
                        ? `${selectedRoom.other_user.first_name[0]}${selectedRoom.other_user.last_name[0]}`
                        : '?'}
                    </Avatar>
                  ) : (
                    <NumberOutlined style={{ fontSize: 20, color: '#666' }} />
                  )}
                  <div>
                    <Text strong style={{ fontSize: 15 }}>
                      {selectedRoom?.type === 'direct' && selectedRoom?.other_user
                        ? `${selectedRoom.other_user.first_name} ${selectedRoom.other_user.last_name}`
                        : selectedRoom?.name || 'Unnamed Channel'}
                    </Text>
                    {/* Status hidden for now */}
                  </div>
                </Space>
                <Space>
                  <Tooltip title="Search messages">
                    <Button
                      type="text"
                      icon={<SearchOutlined />}
                      onClick={() => setMessageSearchVisible(true)}
                    />
                  </Tooltip>
                  {selectedRoom && (
                    <>
                      <Tooltip title="Room info">
                        <Button
                          type="text"
                          icon={<InfoCircleOutlined />}
                          onClick={() => setRoomInfoVisible(true)}
                        />
                      </Tooltip>
                      <Tooltip title="Pinned messages">
                        <Button
                          type="text"
                          icon={<PushpinOutlined />}
                          onClick={() => setPinnedMessagesModalVisible(true)}
                        />
                      </Tooltip>
                    </>
                  )}
                  {isConnecting && (
                    <Spin size="small" />
                  )}
                  {connectionError && (
                    <Tooltip 
                      title={
                        <div>
                          <div>{connectionError}</div>
                          <div style={{ marginTop: 4, fontSize: 11 }}>
                            <Button 
                              type="link" 
                              size="small" 
                              onClick={() => {
                                reconnect();
                              }}
                              style={{ padding: 0, height: 'auto', color: '#fff' }}
                            >
                              Retry Connection
                            </Button>
                          </div>
                        </div>
                      }
                      mouseEnterDelay={0.5}
                    >
                      <Button type="text" icon={<ExclamationCircleOutlined />} danger />
                    </Tooltip>
                  )}
                </Space>
              </div>

              {/* Messages Area */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '20px 24px',
                  background: '#ffffff',
                  scrollBehavior: 'smooth',
                }}
              >
                {messagesLoading ? (
                  <Spin style={{ display: 'block', textAlign: 'center', marginTop: 50 }} />
                ) : messages.length === 0 ? (
                  <Empty description="No messages yet" style={{ marginTop: 50 }} />
                ) : (
                  <>
                    {messages.map((message: any, index: number) => {
                      // System messages display differently
                      if (message.content_type === 'system') {
                        return (
                          <div
                            key={message.id}
                            style={{
                              textAlign: 'center',
                              margin: '16px 0',
                              padding: '8px 0',
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 12,
                                fontStyle: 'italic',
                                backgroundColor: '#f8f9fa',
                                padding: '6px 16px',
                                borderRadius: '16px',
                                display: 'inline-block',
                                color: '#6c757d',
                                fontWeight: 400,
                              }}
                            >
                              {renderMarkdown(message.content)}
                            </Text>
                          </div>
                        );
                      }

                      const prevMessage = index > 0 ? messages[index - 1] : null;
                      const showAvatar = !prevMessage || prevMessage.user_id !== message.user_id || 
                        dayjs(message.created_at).diff(dayjs(prevMessage.created_at), 'minute') > 5;
                      
                      const isOwnMessage = message.user_id === user?.id;
                      
                      return (
                        <div
                          key={message.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                            alignItems: 'flex-end',
                            marginBottom: showAvatar ? 16 : 4,
                            padding: '0 8px',
                            width: '100%',
                          }}
                          onMouseEnter={(e) => {
                            const target = e.currentTarget;
                            const actions = target.querySelector('.message-actions') as HTMLElement;
                            if (actions) actions.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            const target = e.currentTarget;
                            const actions = target.querySelector('.message-actions') as HTMLElement;
                            if (actions) actions.style.opacity = '0';
                          }}
                        >
                          {!isOwnMessage && showAvatar && (
                            <div style={{ marginRight: 8, flexShrink: 0, alignSelf: 'flex-start', marginTop: 4 }}>
                              {renderAvatar(message, 36, themeAccentColor)}
                            </div>
                          )}
                          {!isOwnMessage && !showAvatar && (
                            <div style={{ width: 44, flexShrink: 0 }} />
                          )}
                          
                          {isOwnMessage && (
                            <div style={{ width: 44, flexShrink: 0 }} />
                          )}
                          
                          <div 
                            style={{ 
                              maxWidth: '70%',
                              minWidth: '120px',
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                            }}
                          >
                            {showAvatar && !isOwnMessage && (
                              <div style={{ marginBottom: 4, paddingLeft: 4 }}>
                                <Space size={4}>
                                  <Text strong style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                                    {message.user?.first_name || message.first_name} {message.user?.last_name || message.last_name}
                                  </Text>
                                  {message.is_pinned && (
                                    <PushpinOutlined style={{ color: '#faad14', fontSize: 12 }} />
                                  )}
                                </Space>
                              </div>
                            )}
                            
                            <div
                              style={{
                                position: 'relative',
                                backgroundColor: isOwnMessage 
                                  ? (message.is_pinned ? '#fff9e6' : '#1890ff')
                                  : (message.is_pinned ? '#fff9e6' : '#f1f3f5'),
                                borderRadius: isOwnMessage 
                                  ? (message.is_pinned ? '16px 16px 4px 16px' : '16px 16px 4px 16px')
                                  : (message.is_pinned ? '16px 16px 16px 4px' : '16px 16px 16px 4px'),
                                padding: '8px 12px',
                                borderLeft: message.is_pinned && !isOwnMessage ? '4px solid #faad14' : 'none',
                                borderRight: message.is_pinned && isOwnMessage ? '4px solid #faad14' : 'none',
                                boxShadow: message.is_pinned 
                                  ? '0 2px 8px rgba(250, 173, 20, 0.2)' 
                                  : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <Paragraph
                                style={{
                                  margin: 0,
                                  fontSize: 15,
                                  lineHeight: '1.4',
                                  color: isOwnMessage ? '#ffffff' : '#1a1a2e',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontWeight: 400,
                                }}
                              >
                                <span>{renderMarkdown(message.content)}</span>
                              </Paragraph>
                              {message.attachment_url && (
                                <div style={{ marginTop: 8 }}>
                                  <a
                                    href={`/api/chat/attachments/${message.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '8px 12px',
                                      background: isOwnMessage ? 'rgba(255,255,255,0.2)' : '#ffffff',
                                      border: `1px solid ${isOwnMessage ? 'rgba(255,255,255,0.3)' : '#e9ecef'}`,
                                      borderRadius: '8px',
                                      textDecoration: 'none',
                                      color: isOwnMessage ? '#ffffff' : '#1264a3',
                                      transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = isOwnMessage ? 'rgba(255,255,255,0.3)' : '#f8f9fa';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = isOwnMessage ? 'rgba(255,255,255,0.2)' : '#ffffff';
                                    }}
                                  >
                                    {message.attachment_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                      <FileImageOutlined style={{ fontSize: 18 }} />
                                    ) : message.attachment_url.match(/\.pdf$/i) ? (
                                      <FilePdfOutlined style={{ fontSize: 18 }} />
                                    ) : (
                                      <FileOutlined style={{ fontSize: 18 }} />
                                    )}
                                    <Text style={{ color: isOwnMessage ? '#ffffff' : '#1264a3' }}>
                                      {message.attachment_name || message.attachment_url}
                                    </Text>
                                  </a>
                                </div>
                              )}
                            </div>
                            
                            {/* Thread Replies */}
                            {message.thread_replies && message.thread_replies.length > 0 && (
                                <div style={{ 
                                  marginTop: 8, 
                                  marginLeft: 0, 
                                  paddingLeft: 12, 
                                  borderLeft: '3px solid #e9ecef',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  padding: '10px 12px',
                                  width: '100%',
                                }}>
                                  <Text type="secondary" style={{ fontSize: 12, marginBottom: 10, display: 'block', fontWeight: 500, color: '#6c757d' }}>
                                    {message.thread_replies.length} {message.thread_replies.length === 1 ? 'reply' : 'replies'}
                                  </Text>
                                  {message.thread_replies.map((reply: any, replyIndex: number) => (
                                    <div key={reply.id} style={{ marginBottom: replyIndex < message.thread_replies.length - 1 ? 12 : 0, display: 'flex', gap: 10 }}>
                                      <Avatar size={28} style={{ backgroundColor: themeAccentColor, flexShrink: 0 }}>
                                        {reply.first_name?.[0]}{reply.last_name?.[0]}
                                      </Avatar>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                          <Text strong style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                                            {reply.first_name} {reply.last_name}
                                          </Text>
                                          <Text type="secondary" style={{ fontSize: 11, color: '#8b8b8b' }}>
                                            {dayjs(reply.created_at).format('h:mm A')}
                                          </Text>
                                        </div>
                                        <Text style={{ fontSize: 14, lineHeight: '1.5', color: '#2c2c2c' }}>
                                          {renderMarkdown(reply.content)}
                                        </Text>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                            )}
                            
                            {message.read_count > 0 && (
                              <div style={{ marginTop: 4, paddingLeft: 4, paddingRight: 4 }}>
                                <Text type="secondary" style={{ fontSize: 11, color: '#8b8b8b' }}>
                                  Read by {message.read_count} {message.read_count === 1 ? 'person' : 'people'}
                                </Text>
                              </div>
                            )}
                            
                            {showAvatar && (
                              <div style={{ marginTop: 4, paddingLeft: 4, paddingRight: 4 }}>
                                <Text type="secondary" style={{ fontSize: 11, color: '#8b8b8b' }}>
                                  {dayjs(message.created_at).format('h:mm A')}
                                </Text>
                              </div>
                            )}
                            
                            {/* Link Previews */}
                            {extractUrls(message.content).map((url, idx) => (
                              <div key={idx} style={{ marginTop: 8, maxWidth: '100%' }}>
                                <LinkPreview url={url} />
                              </div>
                            ))}
                            
                            <div
                              className="message-actions"
                              style={{
                                position: 'absolute',
                                right: isOwnMessage ? 'auto' : -50,
                                left: isOwnMessage ? -50 : 'auto',
                                top: 0,
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                                display: 'flex',
                                gap: 2,
                                backgroundColor: '#ffffff',
                                padding: '4px 6px',
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 10,
                              }}
                            >
                                <Popover
                                  content={
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      {['👍', '❤️', '😂', '😮', '😢', '🙌', '👏', '🔥', '✅', '❌'].map((emoji) => (
                                        <Button
                                          key={emoji}
                                          type="text"
                                          onClick={() => {
                                            if (socket) {
                                              socket.emit('message:react', { message_id: message.id, emoji });
                                            }
                                            setReactionPopoverVisible(null);
                                          }}
                                          style={{ fontSize: 20 }}
                                        >
                                          {emoji}
                                        </Button>
                                      ))}
                                    </div>
                                  }
                                  trigger="click"
                                  visible={reactionPopoverVisible === message.id}
                                  onVisibleChange={(visible) => setReactionPopoverVisible(visible ? message.id : null)}
                                >
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<SmileOutlined />}
                                  />
                                </Popover>
                                <Tooltip title={message.thread_replies && message.thread_replies.length > 0 ? `${message.thread_replies.length} replies` : 'Reply in thread'}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CommentOutlined />}
                                    onClick={() => {
                                      // Set thread root for replying
                                      setThreadRootId(message.id);
                                      setEditingMessageId(null);
                                      setEditMessageText('');
                                    }}
                                    style={threadRootId === message.id ? { backgroundColor: '#e6f7ff' } : {}}
                                  />
                                </Tooltip>
                                {(user?.role === 'admin') && (
                                  <Tooltip title={message.is_pinned ? 'Unpin message' : 'Pin message'}>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<PushpinOutlined style={{ color: message.is_pinned ? '#1890ff' : undefined }} />}
                                      onClick={() => {
                                        if (socket) {
                                          socket.emit('message:pin', { 
                                            message_id: message.id, 
                                            pin: !message.is_pinned 
                                          });
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                <Dropdown
                                  overlay={
                                    <Menu>
                                      {canEditDeleteMessage(message) && (
                                        <>
                                          <Menu.Item
                                            key="edit"
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                              setEditingMessageId(message.id);
                                              setEditMessageText(message.content);
                                            }}
                                          >
                                            Edit
                                          </Menu.Item>
                                          <Menu.Item
                                            key="delete"
                                            icon={<DeleteOutlined />}
                                            danger
                                            onClick={() => {
                                              if (socket) {
                                                socket.emit('message:delete', { message_id: message.id });
                                              }
                                            }}
                                          >
                                            Delete
                                          </Menu.Item>
                                        </>
                                      )}
                                    </Menu>
                                  }
                                  trigger={['click']}
                                >
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<MoreOutlined />}
                                  />
                                </Dropdown>
                              </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Typing Indicator */}
              {typingUsers.size > 0 && (
                <div style={{ padding: '8px 16px', fontStyle: 'italic', color: '#999', fontSize: 13 }}>
                  {Array.from(typingUsers).map((userId: string) => {
                    const typingUser = availableUsers.find((u: any) => u.id === userId);
                    return typingUser
                      ? `${typingUser.first_name} ${typingUser.last_name} is typing...`
                      : 'Someone is typing...';
                  })}
                </div>
              )}

              {/* Input Area */}
              <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #e0e0e0' }}>
                {selectedFiles.length > 0 && (
                  <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          background: '#f0f0f0',
                          borderRadius: 4,
                        }}
                      >
                        <PaperClipOutlined />
                        <Text style={{ fontSize: 12 }}>{file.name}</Text>
                        <Button
                          type="text"
                          size="small"
                          onClick={() => removeFile(index)}
                          style={{ padding: 0, minWidth: 'auto' }}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        multiple
                        style={{ display: 'none' }}
                      />
                      <Button
                        icon={<PaperClipOutlined />}
                        onClick={() => fileInputRef.current?.click()}
                        style={{ flexShrink: 0 }}
                      />
                      {threadRootId && (
                        <div style={{ 
                          marginBottom: 8, 
                          padding: '8px 12px', 
                          backgroundColor: '#e6f7ff', 
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <Text style={{ fontSize: 12 }}>
                            Replying to thread
                          </Text>
                          <Button
                            type="text"
                            size="small"
                            onClick={() => setThreadRootId(null)}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            ×
                          </Button>
                        </div>
                      )}
                      <div style={{ position: 'relative', flex: 1 }}>
                        <Input
                          placeholder={threadRootId ? `Reply in thread...` : `Message ${selectedRoom?.type === 'direct' && selectedRoom?.other_user ? selectedRoom.other_user.first_name : selectedRoom?.name || 'channel'} (use @ to mention)`}
                          value={messageText}
                          onChange={(e) => {
                            const value = e.target.value;
                            setMessageText(value);
                            if (socket && selectedRoomId) {
                              socket.emit('typing:start', { room_id: selectedRoomId });
                            }
                            
                            // Check for @mention
                            const cursorPos = e.target.selectionStart || value.length;
                            const textBeforeCursor = value.substring(0, cursorPos);
                            const lastAtIndex = textBeforeCursor.lastIndexOf('@');
                            
                            if (lastAtIndex !== -1) {
                              const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
                              // Check if we're still typing a mention (no space after @)
                              if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                                const query = textAfterAt.toLowerCase();
                                const filtered = availableUsers
                                  .filter((u: any) => 
                                    u.id !== user?.id && 
                                    (u.first_name?.toLowerCase().includes(query) || 
                                     u.last_name?.toLowerCase().includes(query) ||
                                     u.email?.toLowerCase().includes(query))
                                  )
                                  .slice(0, 5)
                                  .map((u: any) => ({
                                    value: `@${u.first_name} ${u.last_name}`,
                                    label: `${u.first_name} ${u.last_name}`,
                                    user: u
                                  }));
                                setMentionOptions(filtered);
                                setShowMentionDropdown(filtered.length > 0);
                                setMentionCursorPosition(lastAtIndex);
                              } else {
                                setShowMentionDropdown(false);
                              }
                            } else {
                              setShowMentionDropdown(false);
                            }
                          }}
                          onPressEnter={(e) => {
                            if (!e.shiftKey && !showMentionDropdown) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          style={{ flex: 1 }}
                        />
                        {showMentionDropdown && mentionOptions.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '100%',
                              left: 0,
                              right: 0,
                              marginBottom: 4,
                              background: '#fff',
                              border: '1px solid #e0e0e0',
                              borderRadius: 4,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              maxHeight: 200,
                              overflowY: 'auto',
                            }}
                          >
                            {mentionOptions.map((option: any, idx: number) => (
                              <div
                                key={idx}
                                onClick={() => {
                                  const textBeforeMention = messageText.substring(0, mentionCursorPosition);
                                  const textAfterMention = messageText.substring(mentionCursorPosition).replace(/@[\w\s]*/, '');
                                  const newText = `${textBeforeMention}${option.value} ${textAfterMention}`;
                                  setMessageText(newText);
                                  setShowMentionDropdown(false);
                                  setMentionOptions([]);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  borderBottom: idx < mentionOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                }}
                                onMouseEnter={(e) => {
                                  (e.currentTarget as HTMLElement).style.backgroundColor = '#f5f5f5';
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                }}
                              >
                                <Avatar size={24} style={{ backgroundColor: '#1890ff' }}>
                                  {option.user.first_name?.[0]}{option.user.last_name?.[0]}
                                </Avatar>
                                <div>
                                  <div style={{ fontWeight: 500 }}>{option.user.first_name} {option.user.last_name}</div>
                                  <div style={{ fontSize: 12, color: '#999' }}>{option.user.email}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Popover
                        content={
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', width: 200 }}>
                            {['👍', '❤️', '😂', '😮', '😢', '🙌', '👏', '🔥', '✅', '❌', '😊', '😍', '🤔', '👀', '🎉', '💯'].map((emoji) => (
                              <Button
                                key={emoji}
                                type="text"
                                onClick={() => insertEmoji(emoji)}
                                style={{ fontSize: 20 }}
                              >
                                {emoji}
                              </Button>
                            ))}
                          </div>
                        }
                        trigger="click"
                        visible={emojiPickerVisible}
                        onVisibleChange={setEmojiPickerVisible}
                      >
                        <Button
                          icon={<SmileOutlined />}
                          style={{ flexShrink: 0 }}
                        />
                      </Popover>
                    </div>
                  </div>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={sendMessageMutation.isLoading}
                    disabled={!messageText.trim() && selectedFiles.length === 0}
                    style={{ flexShrink: 0, height: '32px' }}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <Empty
              description="Select a conversation to start chatting"
              style={{ marginTop: 100 }}
            />
          )}
        </Content>
      </Layout>

      {/* Create Room Modal - Slack-like UI */}
      <Modal
        title={null}
        open={createRoomModalVisible}
        onCancel={() => {
          setCreateRoomModalVisible(false);
          setSelectedUserIds([]);
          setUserSearchQuery('');
          setShowGroupNameInput(false);
          setGroupName('');
        }}
        footer={null}
        width={520}
        style={{ top: 100 }}
        closable={true}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Text strong style={{ fontSize: 18 }}>
              New message
            </Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search for people..."
              prefix={<SearchOutlined style={{ color: '#999' }} />}
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              size="large"
              style={{ borderRadius: 4 }}
              autoFocus
            />
          </div>

          {selectedUserIds.length > 0 && (
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedUserIds.map((userId) => {
                const selectedUser = availableUsers.find((u: any) => u.id === userId);
                if (!selectedUser) return null;
                return (
                  <div
                    key={userId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 8px',
                      background: '#e6f7ff',
                      border: '1px solid #91d5ff',
                      borderRadius: 16,
                      fontSize: 13,
                    }}
                  >
                    <Avatar size={20} style={{ backgroundColor: '#1890ff' }}>
                      {selectedUser.first_name[0]}{selectedUser.last_name[0]}
                    </Avatar>
                    <Text style={{ fontSize: 13 }}>
                      {selectedUser.first_name} {selectedUser.last_name}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleUserSelect(userId)}
                      style={{
                        padding: 0,
                        minWidth: 'auto',
                        height: 'auto',
                        lineHeight: 1,
                        color: '#1890ff',
                      }}
                    >
                      ×
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid #f0f0f0',
              borderRadius: 4,
            }}
          >
            {filteredAvailableUsers.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                <Text type="secondary">No users found</Text>
              </div>
            ) : (
              <List
                dataSource={filteredAvailableUsers}
                renderItem={(u: any) => {
                  const isSelected = selectedUserIds.includes(u.id);
                  return (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        padding: '12px 16px',
                        backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onClick={() => handleUserSelect(u.id)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#1890ff' }}>
                            {u.first_name[0]}{u.last_name[0]}
                          </Avatar>
                        }
                        title={
                          <Space>
                            <Text strong style={{ fontSize: 14 }}>
                              {u.first_name} {u.last_name}
                            </Text>
                            {isSelected && (
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            )}
                          </Space>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {u.email}
                          </Text>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setCreateRoomModalVisible(false);
                setSelectedUserIds([]);
                setUserSearchQuery('');
                setShowGroupNameInput(false);
                setGroupName('');
                setIsPrivateGroup(false);
              }}
            >
              Cancel
            </Button>
            {selectedUserIds.length === 1 ? (
              <Button
                type="primary"
                onClick={() => handleCreateRoom('direct')}
                loading={createRoomMutation.isLoading}
                disabled={selectedUserIds.length !== 1}
              >
                Start Direct Message
              </Button>
            ) : selectedUserIds.length > 1 ? (
              <>
                {showGroupNameInput ? (
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Input
                        placeholder="Enter group name..."
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        onPressEnter={() => {
                          if (groupName.trim()) {
                            handleCreateRoom('group', groupName.trim());
                          }
                        }}
                        autoFocus
                        style={{ flex: 1 }}
                      />
                      <Button
                        onClick={() => {
                          setShowGroupNameInput(false);
                          setGroupName('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          if (groupName.trim()) {
                            handleCreateRoom('group', groupName.trim());
                          }
                        }}
                        loading={createRoomMutation.isLoading}
                        disabled={!groupName.trim()}
                      >
                        Create
                      </Button>
                    </div>
                    <Checkbox
                      checked={isPrivateGroup}
                      onChange={(e) => setIsPrivateGroup(e.target.checked)}
                    >
                      Private group (users must be invited)
                    </Checkbox>
                  </div>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => setShowGroupNameInput(true)}
                    disabled={selectedUserIds.length < 2}
                  >
                    Create Group ({selectedUserIds.length})
                  </Button>
                )}
              </>
            ) : (
              <Button type="primary" disabled>
                Select people
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* Invite Users Modal */}
      <Modal
        title="Invite Users to Channel"
        visible={inviteModalVisible}
        onCancel={() => {
          setInviteModalVisible(false);
          setInviteRoomId(null);
          setInviteUserIds([]);
          setInviteSearchQuery('');
        }}
        onOk={async () => {
          if (inviteRoomId && inviteUserIds.length > 0) {
            try {
              await api.post(`/api/chat/rooms/${inviteRoomId}/invite`, {
                user_ids: inviteUserIds
              });
              // API call succeeded, now update UI (don't fail if these throw)
              try {
                await queryClient.invalidateQueries('chat-rooms');
              } catch (queryError) {
                console.warn('Query invalidation error (non-critical):', queryError);
              }
              setInviteModalVisible(false);
              setInviteRoomId(null);
              setInviteUserIds([]);
              setInviteSearchQuery('');
              message.success(`Successfully invited ${inviteUserIds.length} user(s)`);
            } catch (error: any) {
              console.error('Failed to invite users:', error);
              // Only show error if it's an API error (has response), not a query error
              if (error.response) {
                message.error(error.response?.data?.error || 'Failed to invite users');
              } else {
                // If no response, it might be a network error - still show error
                message.error('Failed to invite users');
              }
            }
          }
        }}
        okText="Invite"
        okButtonProps={{ disabled: inviteUserIds.length === 0 }}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            value={inviteSearchQuery}
            onChange={(e) => setInviteSearchQuery(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {availableUsers
            .filter((u: any) => {
              if (inviteSearchQuery) {
                const query = inviteSearchQuery.toLowerCase();
                return (
                  u.first_name.toLowerCase().includes(query) ||
                  u.last_name.toLowerCase().includes(query) ||
                  u.email.toLowerCase().includes(query)
                );
              }
              return true;
            })
            .filter((u: any) => u.id !== user?.id)
            .map((u: any) => {
              const isSelected = inviteUserIds.includes(u.id);
              return (
                <div
                  key={u.id}
                  onClick={() => {
                    if (isSelected) {
                      setInviteUserIds(inviteUserIds.filter(id => id !== u.id));
                    } else {
                      setInviteUserIds([...inviteUserIds, u.id]);
                    }
                  }}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <Avatar style={{ backgroundColor: '#1890ff' }}>
                    {u.first_name[0]}{u.last_name[0]}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <Text strong>{u.first_name} {u.last_name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{u.email}</Text>
                  </div>
                  {isSelected && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                </div>
              );
            })}
        </div>
      </Modal>

      {/* Browse All Groups Modal */}
      <Modal
        title="Browse Channels"
        visible={publicGroupsModalVisible}
        onCancel={() => {
          setPublicGroupsModalVisible(false);
          setPublicGroupsSearchQuery('');
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search channels..."
            prefix={<SearchOutlined />}
            value={publicGroupsSearchQuery}
            onChange={(e) => {
              setPublicGroupsSearchQuery(e.target.value);
            }}
            allowClear
          />
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {publicGroupsLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin />
            </div>
          ) : publicGroups.length === 0 ? (
            <Empty description={publicGroupsSearchQuery ? "No channels found matching your search" : "No channels available"} />
          ) : (
            <List
              dataSource={publicGroups}
              renderItem={(room: any) => (
                <List.Item
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  actions={[
                    room.is_member ? (
                      <Button type="text" disabled>
                        Joined
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        onClick={async () => {
                          try {
                            await api.post(`/api/chat/rooms/${room.id}/join`);
                            // API call succeeded, now update UI (don't fail if these throw)
                            try {
                              await queryClient.invalidateQueries('chat-rooms');
                              await refetchPublicGroups();
                            } catch (queryError) {
                              console.warn('Query invalidation error (non-critical):', queryError);
                            }
                            message.success(`Joined "${room.name}"`);
                          } catch (error: any) {
                            console.error('Failed to join room:', error);
                            // Only show error if it's an API error (has response), not a query error
                            if (error.response) {
                              message.error(error.response?.data?.error || 'Failed to join group');
                            } else {
                              // If no response, it might be a network error - still show error
                              message.error('Failed to join group');
                            }
                          }
                        }}
                      >
                        Join
                      </Button>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar style={{ backgroundColor: '#1890ff' }}>
                        <UnlockOutlined />
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>{room.name || 'Unnamed Group'}</Text>
                        {room.is_member && (
                          <Badge status="success" text="Member" />
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        {room.description && (
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                            {room.description}
                          </Text>
                        )}
                        <Space direction="vertical" size={2}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {room.member_count || 0} {room.member_count === 1 ? 'member' : 'members'}
                          </Text>
                          {room.creator_first_name && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Created by {room.creator_first_name} {room.creator_last_name}
                            </Text>
                          )}
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>

      {/* Message Search Modal */}
      <Modal
        title="Search Messages"
        open={messageSearchVisible}
        onCancel={() => {
          setMessageSearchVisible(false);
          setMessageSearchQuery('');
          setSearchFilterUserId(undefined);
          setSearchDateRange(null);
          setSearchContentType(undefined);
        }}
        footer={null}
        width={900}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input
            placeholder="Search messages..."
            prefix={<SearchOutlined />}
            value={messageSearchQuery}
            onChange={(e) => setMessageSearchQuery(e.target.value)}
            allowClear
            onPressEnter={() => {
              // Trigger search
            }}
          />
          <Space wrap>
            <Select
              placeholder="Filter by user"
              allowClear
              style={{ width: 200 }}
              value={searchFilterUserId}
              onChange={setSearchFilterUserId}
              showSearch
              filterOption={(input, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableUsers.map((u: any) => (
                <Select.Option key={u.id} value={u.id} label={`${u.first_name} ${u.last_name}`}>
                  <Space>
                    <Avatar size={20} style={{ backgroundColor: '#1890ff' }}>
                      {u.first_name?.[0]}{u.last_name?.[0]}
                    </Avatar>
                    {u.first_name} {u.last_name}
                  </Space>
                </Select.Option>
              ))}
            </Select>
            <DatePicker.RangePicker
              placeholder={['Start date', 'End date']}
              value={searchDateRange}
              onChange={(dates) => setSearchDateRange(dates as any)}
              style={{ width: 250 }}
            />
            <Select
              placeholder="Content type"
              allowClear
              style={{ width: 150 }}
              value={searchContentType}
              onChange={setSearchContentType}
            >
              <Select.Option value="text">Text</Select.Option>
              <Select.Option value="image">Image</Select.Option>
              <Select.Option value="file">File</Select.Option>
              <Select.Option value="system">System</Select.Option>
            </Select>
            {user?.role === 'admin' && (
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams({ q: messageSearchQuery || '' });
                    if (selectedRoomId) params.append('room_id', selectedRoomId);
                    if (searchFilterUserId) params.append('user_id', searchFilterUserId);
                    if (searchDateRange && searchDateRange[0] && searchDateRange[1]) {
                      params.append('start_date', searchDateRange[0].format('YYYY-MM-DD'));
                      params.append('end_date', searchDateRange[1].format('YYYY-MM-DD'));
                    }
                    if (searchContentType) params.append('content_type', searchContentType);
                    
                    const response = await api.get(`/api/chat/messages/search?${params}`);
                    const messages = response.data.messages || [];
                    
                    // Export as CSV
                    const csv = [
                      ['Date', 'Room', 'User', 'Content', 'Type'].join(','),
                      ...messages.map((m: any) => [
                        dayjs(m.created_at).format('YYYY-MM-DD HH:mm:ss'),
                        `"${m.room_name || 'Direct Message'}"`,
                        `"${m.first_name} ${m.last_name}"`,
                        `"${(m.content || '').replace(/"/g, '""')}"`,
                        m.content_type || 'text'
                      ].join(','))
                    ].join('\n');
                    
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `chat-history-${dayjs().format('YYYY-MM-DD')}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    
                    message.success('Chat history exported successfully');
                  } catch (error: any) {
                    console.error('Export error:', error);
                    message.error('Failed to export chat history');
                  }
                }}
              >
                Export (Admin)
              </Button>
            )}
          </Space>
          {messageSearchQuery && (
            <MessageSearchResults 
              query={messageSearchQuery} 
              roomId={selectedRoomId || undefined}
              userId={searchFilterUserId}
              dateRange={searchDateRange}
              contentType={searchContentType}
              onMessageClick={(message: any) => {
                // Navigate to room and scroll to message
                if (message.room_id !== selectedRoomId) {
                  setSelectedRoomId(message.room_id);
                }
                setMessageSearchVisible(false);
                setMessageSearchQuery('');
                setSearchFilterUserId(undefined);
                setSearchDateRange(null);
                setSearchContentType(undefined);
              }}
            />
          )}
        </Space>
      </Modal>

      {/* Pinned Messages Modal */}
      <Modal
        title="Pinned Messages"
        open={pinnedMessagesModalVisible}
        onCancel={() => setPinnedMessagesModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedRoomId ? (
          <PinnedMessagesList roomId={selectedRoomId} />
        ) : (
          <Empty description="Select a room to view pinned messages" />
        )}
      </Modal>


      {/* Room Info Modal */}
      <Modal
        title={selectedRoom?.type === 'direct' ? 'Direct Message Info' : 'Room Info'}
        open={roomInfoVisible}
        onCancel={() => setRoomInfoVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRoomId ? (
          <RoomInfoPanel 
            roomId={selectedRoomId} 
            room={selectedRoom}
            onNotificationSettingsClick={() => {
              setRoomInfoVisible(false);
              setNotificationSettingsModalVisible(true);
            }}
          />
        ) : (
          <Empty description="Select a room to view info" />
        )}
      </Modal>

      {/* Theme Customization Modal */}
      <Modal
        title="Customize Theme"
        open={themeModalVisible}
        onOk={async () => {
          try {
            await api.put('/api/users/preferences', {
              theme_color: themeColor,
              theme_accent_color: themeAccentColor,
            });
            message.success('Theme updated successfully');
            setThemeModalVisible(false);
          } catch (error: any) {
            const errorMsg = error.response?.data?.details 
              ? error.response.data.details.map((d: any) => d.msg).join(', ')
              : error.response?.data?.error || 'Failed to update theme';
            message.error(errorMsg);
            console.error('Theme update error:', error.response?.data);
          }
        }}
        onCancel={() => setThemeModalVisible(false)}
        okText="Save"
        cancelText="Cancel"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Sidebar Color
            </Text>
            <ColorPicker
              value={themeColor}
              onChange={(color) => {
                // Ensure we get a 6-digit hex string (remove alpha if present)
                const hex = typeof color === 'string' ? color : color.toHexString();
                // Remove alpha channel if present (8 chars -> 6 chars)
                const hex6 = hex.length === 9 ? hex.substring(0, 7) : hex;
                setThemeColor(hex6);
              }}
              showText
              format="hex"
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Accent Color (Avatars, Buttons, Links)
            </Text>
            <ColorPicker
              value={themeAccentColor}
              onChange={(color) => {
                // Ensure we get a 6-digit hex string (remove alpha if present)
                const hex = typeof color === 'string' ? color : color.toHexString();
                // Remove alpha channel if present (8 chars -> 6 chars)
                const hex6 = hex.length === 9 ? hex.substring(0, 7) : hex;
                setThemeAccentColor(hex6);
              }}
              showText
              format="hex"
            />
          </div>
          <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Preview: Your sidebar and accent colors will update in real-time. Click "Save" to persist your preferences.
            </Text>
          </div>
        </Space>
      </Modal>

      {/* Custom Status Modal */}
      <Modal
        title="Set Custom Status"
        open={customStatusModalVisible}
        onCancel={() => {
          setCustomStatusModalVisible(false);
          setCustomStatusText('');
        }}
        onOk={async () => {
          try {
            await api.put('/api/chat/user/presence', {
              custom_status: customStatusText,
            });
            message.success('Status updated');
            setCustomStatusModalVisible(false);
            queryClient.invalidateQueries('user-presence');
          } catch (error: any) {
            console.error('Failed to update status:', error);
            message.error('Failed to update status');
          }
        }}
        width={500}
      >
        <Input
          placeholder="What's your status? (e.g., In a meeting, Working from home)"
          value={customStatusText}
          onChange={(e) => setCustomStatusText(e.target.value)}
          maxLength={100}
          allowClear
        />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          This will be visible to other users
        </Text>
      </Modal>

      {/* Notification Settings Modal */}
      <Modal
        title="Notification Settings"
        open={notificationSettingsModalVisible}
        onCancel={() => setNotificationSettingsModalVisible(false)}
        footer={null}
        width={500}
      >
        {selectedRoomId && (
          <NotificationSettingsPanel roomId={selectedRoomId} />
        )}
      </Modal>

      {/* Invite Confirmation Modal */}
      <Modal
        title="Invite Users to Room"
        open={inviteConfirmationVisible}
        onOk={handleInviteAndResend}
        onCancel={() => {
          setInviteConfirmationVisible(false);
          setUsersToInvite([]);
          setPendingMessage(null);
        }}
        okText="Invite & Send"
        cancelText="Cancel"
      >
        <div>
          <Text>
            The following users are mentioned but not in this room. Would you like to invite them?
          </Text>
          <List
            style={{ marginTop: 16 }}
            dataSource={usersToInvite}
            renderItem={(user: any) => (
              <List.Item>
                <List.Item.Meta
                  avatar={renderAvatar(user, 32, themeAccentColor)}
                  title={`${user.first_name} ${user.last_name}`}
                />
              </List.Item>
            )}
          />
        </div>
      </Modal>
    </Layout>
  );
};

// Room Info Panel Component
function RoomInfoPanel({ roomId, room, onNotificationSettingsClick }: { roomId: string; room: any; onNotificationSettingsClick?: () => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const { data: membersData, isLoading: membersLoading } = useQuery(
    ['room-members', roomId],
    () => api.get(`/api/chat/rooms/${roomId}/members`).then(res => res.data),
    {
      enabled: !!roomId,
    }
  );

  const { data: pinnedData } = useQuery(
    ['pinned-messages', roomId],
    () => api.get(`/api/chat/rooms/${roomId}/pinned`).then(res => res.data),
    {
      enabled: !!roomId,
    }
  );

  const members = membersData?.members || [];
  const pinnedCount = pinnedData?.messages?.length || 0;

  return (
    <div>
      {/* Room Details */}
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
          {room?.type === 'direct' && room?.other_user
            ? `${room.other_user.first_name} ${room.other_user.last_name}`
            : room?.name || 'Unnamed Channel'}
        </Text>
        {room?.description && (
          <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
            {room.description}
          </Text>
        )}
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {members.length} {members.length === 1 ? 'member' : 'members'}
          </Text>
          {pinnedCount > 0 && (
            <>
              <Text type="secondary">•</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {pinnedCount} {pinnedCount === 1 ? 'pinned message' : 'pinned messages'}
              </Text>
            </>
          )}
        </Space>
      </div>

      {/* Notification Settings */}
      {onNotificationSettingsClick && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="default"
            icon={<BellOutlined />}
            onClick={onNotificationSettingsClick}
            block
          >
            Notification Settings
          </Button>
        </div>
      )}

      {/* Members List */}
      <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
        Members
      </Divider>
      {membersLoading ? (
        <Spin />
      ) : (
        <List
          dataSource={members}
          renderItem={(member: any) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar style={{ backgroundColor: '#1890ff' }}>
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </Avatar>
                }
                title={
                  <Space>
                    <Text strong>{member.first_name} {member.last_name}</Text>
                    {member.role === 'admin' && (
                      <Badge status="success" text="Admin" />
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {member.email}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Joined {dayjs(member.joined_at).format('MMM D, YYYY')}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}

// Message Search Results Component
function MessageSearchResults({ 
  query, 
  roomId, 
  userId,
  dateRange,
  contentType,
  onMessageClick 
}: { 
  query: string; 
  roomId?: string; 
  userId?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
  contentType?: string;
  onMessageClick: (message: any) => void;
}) {
  const { data, isLoading } = useQuery(
    ['message-search', query, roomId, userId, dateRange, contentType],
    () => {
      const params = new URLSearchParams({ q: query });
      if (roomId) params.append('room_id', roomId);
      if (userId) params.append('user_id', userId);
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      if (contentType) params.append('content_type', contentType);
      return api.get(`/api/chat/messages/search?${params}`).then(res => res.data);
    },
    {
      enabled: query.length > 0,
    }
  );

  if (isLoading) return <Spin />;
  if (!data?.messages || data.messages.length === 0) {
    return <Empty description="No messages found" />;
  }

  return (
    <List
      dataSource={data.messages}
      renderItem={(message: any) => (
        <List.Item
          style={{ cursor: 'pointer' }}
          onClick={() => onMessageClick(message)}
        >
          <List.Item.Meta
            avatar={
              <Avatar style={{ backgroundColor: '#1890ff' }}>
                {message.first_name?.[0]}{message.last_name?.[0]}
              </Avatar>
            }
            title={
              <Space>
                <Text strong>{message.first_name} {message.last_name}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {message.room_name || 'Direct Message'}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(message.created_at).format('MMM D, YYYY h:mm A')}
                </Text>
              </Space>
            }
            description={
              <Text style={{ fontSize: 13 }}>
                {message.content.length > 200 
                  ? `${message.content.substring(0, 200)}...` 
                  : message.content}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );
}

// Pinned Messages List Component
function PinnedMessagesList({ roomId }: { roomId: string }) {
  const { data, isLoading, refetch } = useQuery(
    ['pinned-messages', roomId],
    () => api.get(`/api/chat/rooms/${roomId}/pinned`).then(res => res.data),
    {
      enabled: !!roomId,
    }
  );

  if (isLoading) return <Spin />;
  if (!data?.messages || data.messages.length === 0) {
    return <Empty description="No pinned messages" />;
  }

  return (
    <List
      dataSource={data.messages}
      renderItem={(message: any) => (
        <List.Item>
          <List.Item.Meta
            avatar={
              <Avatar style={{ backgroundColor: '#1890ff' }}>
                {message.first_name?.[0]}{message.last_name?.[0]}
              </Avatar>
            }
            title={
              <Space>
                <Text strong>{message.first_name} {message.last_name}</Text>
                {message.pinned_by_first_name && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Pinned by {message.pinned_by_first_name} {message.pinned_by_last_name}
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(message.pinned_at).format('MMM D, YYYY h:mm A')}
                </Text>
              </Space>
            }
            description={
              <Text style={{ fontSize: 13 }}>{message.content}</Text>
            }
          />
        </List.Item>
      )}
    />
  );
}

// Starred Messages List Component
function StarredMessagesList({ onMessageClick }: { onMessageClick: (message: any) => void }) {
  const { data, isLoading } = useQuery(
    'starred-messages',
    () => api.get('/api/chat/messages/starred').then(res => res.data),
  );

  if (isLoading) return <Spin />;
  if (!data?.messages || data.messages.length === 0) {
    return <Empty description="No starred messages" />;
  }

  return (
    <List
      dataSource={data.messages}
      renderItem={(message: any) => (
        <List.Item
          style={{ cursor: 'pointer' }}
          onClick={() => onMessageClick(message)}
        >
          <List.Item.Meta
            avatar={
              <Avatar style={{ backgroundColor: '#1890ff' }}>
                {message.first_name?.[0]}{message.last_name?.[0]}
              </Avatar>
            }
            title={
              <Space>
                <Text strong>{message.first_name} {message.last_name}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {message.room_name || 'Direct Message'}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(message.starred_at).format('MMM D, YYYY h:mm A')}
                </Text>
              </Space>
            }
            description={
              <Text style={{ fontSize: 13 }}>
                {message.content.length > 200 
                  ? `${message.content.substring(0, 200)}...` 
                  : message.content}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );
}

// Notification Settings Panel Component
function NotificationSettingsPanel({ roomId }: { roomId: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [notificationPreference, setNotificationPreference] = useState<string>('all');
  const [muteUntil, setMuteUntil] = useState<dayjs.Dayjs | null>(null);

  // Fetch current notification settings
  const { data: roomData } = useQuery(
    ['chat-rooms'],
    () => api.get('/api/chat/rooms').then(res => res.data),
  );

  const currentRoom = roomData?.rooms?.find((r: any) => r.id === roomId);
  const currentPreference = currentRoom?.notification_preference || 'all';

  useEffect(() => {
    setNotificationPreference(currentPreference);
  }, [currentPreference]);

  const updateNotificationPreference = async (preference: string) => {
    try {
      await api.put(`/api/chat/rooms/${roomId}/notification-preference`, {
        notification_preference: preference,
      });
      setNotificationPreference(preference);
      message.success('Notification settings updated');
      queryClient.invalidateQueries('chat-rooms');
    } catch (error: any) {
      console.error('Failed to update notification preference:', error);
      message.error('Failed to update notification settings');
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div>
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
          Notify me about:
        </Text>
        <Select
          value={notificationPreference}
          onChange={updateNotificationPreference}
          style={{ width: '100%' }}
        >
          <Select.Option value="all">All messages</Select.Option>
          <Select.Option value="mentions">Only @mentions</Select.Option>
          <Select.Option value="none">Nothing</Select.Option>
        </Select>
      </div>
      <div>
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
          Mute notifications until:
        </Text>
        <DatePicker
          showTime
          value={muteUntil}
          onChange={(date) => {
            setMuteUntil(date);
            if (date) {
              // Set mute until this time
              api.put(`/api/chat/rooms/${roomId}/mute`, {
                mute_until: date.toISOString(),
              }).then(() => {
                message.success('Room muted');
              });
            }
          }}
          style={{ width: '100%' }}
          placeholder="Select date and time"
          allowClear
        />
      </div>
    </Space>
  );
}

export default Chat;
